import React, { useEffect, useState } from "react";
import { courseList } from "../../api/Courses";
import { getSessions } from "../../api/SessionsService";
import { UpdateSyllabus } from "../../api/SyllabusService";
import { SweetAlertShow } from "../master/Master";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import AcademicHeader from "../Academic/AcademicHeader";

export default function SyllabusUpdate() {
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [selectedSession, setSelectedSession] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");

  const [topics, setTopics] = useState([
    {
      topic: "",
      description: "",
      duration: "",
    },
  ]);

  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const syllabusItem = location.state?.syllabusItem || {};

  // =====================================================
  // GET SUBJECTS FROM COURSE
  // =====================================================
  const getCourseSubjects = (course) => {
    if (!course) {
      return [];
    }

    // Direct array
    if (Array.isArray(course?.subjects)) {
      return course.subjects;
    }

    // subjects_list direct array
    if (Array.isArray(course?.subjects_list)) {
      return course.subjects_list;
    }

    // subjects object -> subjects_list
    if (Array.isArray(course?.subjects?.subjects_list)) {
      return course.subjects.subjects_list;
    }

    // subjects object -> data
    if (Array.isArray(course?.subjects?.data)) {
      return course.subjects.data;
    }

    // subjects_list object -> data
    if (Array.isArray(course?.subjects_list?.data)) {
      return course.subjects_list.data;
    }

    return [];
  };

  // =====================================================
  // PREFILL SYLLABUS DATA
  // =====================================================
  useEffect(() => {
    if (!syllabusItem?.id) {
      return;
    }

    setSelectedSession(
      syllabusItem?.session_id ? String(syllabusItem.session_id) : "",
    );

    setSelectedCourse(
      syllabusItem?.course_id ? String(syllabusItem.course_id) : "",
    );

    setSelectedSubject(
      syllabusItem?.subject_id ? String(syllabusItem.subject_id) : "",
    );

    setTopics([
      {
        topic: syllabusItem?.title || "",
        description: syllabusItem?.description || "",
        duration: syllabusItem?.duration_day || "",
      },
    ]);
  }, [syllabusItem]);

  // =====================================================
  // COURSE LIST
  // =====================================================
  const courseFetch = async () => {
    setLoading(true);

    try {
      const response = await courseList();

      if (response?.data?.status) {
        setCourses(response?.data?.data || []);
      } else {
        setCourses([]);
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const sessionFetch = async () => {
    try {
      const response = await getSessions();

      if (response?.data?.status) {
        setSessions(response?.data?.data || []);
      } else {
        setSessions([]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    courseFetch();
    sessionFetch();
  }, []);

  useEffect(() => {
    if (!selectedCourse || !courses?.length) {
      setSubjects([]);
      return;
    }

    const selectedCourseData = courses.find(
      (course) => String(course?.id) === String(selectedCourse),
    );

    if (!selectedCourseData) {
      setSubjects([]);
      return;
    }

    const courseSubjects = getCourseSubjects(selectedCourseData);

    setSubjects(courseSubjects);

    // Existing syllabus subject select karo
    if (syllabusItem?.subject_id) {
      const subjectExists = courseSubjects.some(
        (subject) => String(subject?.id) === String(syllabusItem.subject_id),
      );

      if (subjectExists) {
        setSelectedSubject(String(syllabusItem.subject_id));
      }
    }
  }, [selectedCourse, courses, syllabusItem?.subject_id]);

  const handleCourseChange = (e) => {
    const courseId = e.target.value;

    setSelectedCourse(courseId);
    setSelectedSubject("");

    if (!courseId) {
      setSubjects([]);
      return;
    }

    const selectedCourseData = courses.find(
      (course) => String(course?.id) === String(courseId),
    );

    const courseSubjects = getCourseSubjects(selectedCourseData);

    setSubjects(courseSubjects);
  };

  const handleSessionChange = (e) => {
    setSelectedSession(e.target.value);
  };

  const handleSubjectChange = (e) => {
    setSelectedSubject(e.target.value);
  };

  const handleTopicChange = (index, field, value) => {
    const updatedTopics = [...topics];
    updatedTopics[index][field] = value;
    setTopics(updatedTopics);
  };

  const updateSyllabus = async (e) => {
    e.preventDefault();

    if (!selectedSession) {
      SweetAlertShow("info", "Warning", "Please select session.");
      return;
    }

    if (!selectedCourse) {
      SweetAlertShow("info", "Warning", "Please select course.");
      return;
    }

    if (!selectedSubject) {
      SweetAlertShow("info", "Warning", "Please select subject.");
      return;
    }

    if (!topics?.[0]?.topic) {
      SweetAlertShow("info", "Warning", "Please enter topic.");
      return;
    }

    if (!topics?.[0]?.duration) {
      SweetAlertShow("info", "Warning", "Please enter duration.");
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();

      formData.append("id", syllabusItem?.id || "");
      formData.append("session_id", selectedSession);
      formData.append("course_id", selectedCourse);
      formData.append("subject_id", selectedSubject);
      formData.append("title", topics?.[0]?.topic || "");
      formData.append("description", topics?.[0]?.description || "");
      formData.append("duration_day", topics?.[0]?.duration || "");

      const response = await UpdateSyllabus(formData);

      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
        navigate(-1);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* =====================================================
          LOADING
      ===================================================== */}
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100"
          style={{ zIndex: 9999, background: "rgba(255,255,255,0.6)" }}
        >
          <div className="sk-chase sk-primary display-1">
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
            <div className="sk-chase-dot" />
          </div>
        </div>
      )}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row mt-4">
                  <AcademicHeader />

                  <div className="col-md-12">
                    <div className="card shadow-sm border-0 mt-2">
                      {/* =====================================================
                          HEADER
                      ===================================================== */}
                      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 bg-label-primary d-flex align-items-center justify-content-center"
                            style={{ width: 44, height: 44 }}
                          >
                            <i className="ri-edit-2-line fs-4" />
                          </div>
                          <div>
                            <h4 className="m-0 text-primary">
                              Update Syllabus
                            </h4>
                            <small className="text-muted">
                              {syllabusItem?.title
                                ? `Editing "${syllabusItem.title}"`
                                : "Update an existing syllabus topic"}
                            </small>
                          </div>
                        </div>

                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => navigate(-1)}
                          disabled={loading}
                        >
                          <i className="ri-arrow-left-line me-1" />
                          Back
                        </button>
                      </div>

                      <div className="card-body">
                        <form onSubmit={updateSyllabus}>
                          <input
                            type="hidden"
                            name="id"
                            value={syllabusItem?.id || ""}
                          />

                          {/* =====================================================
                              SESSION / COURSE / SUBJECT
                          ===================================================== */}
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <span
                              className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: 24, height: 24, fontSize: 12 }}
                            >
                              1
                            </span>
                            <h6 className="mb-0">Classification</h6>
                          </div>

                          <div className="row g-3 mb-4">
                            {/* SESSION */}
                            <div className="col-md-4">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={selectedSession}
                                  onChange={handleSessionChange}
                                  required
                                >
                                  <option value="">Select Session</option>
                                  {sessions?.map((session) => (
                                    <option
                                      key={session?.id}
                                      value={session?.id}
                                    >
                                      {session?.session}
                                      {String(session?.status) === "1"
                                        ? " : Active"
                                        : " : Inactive"}
                                    </option>
                                  ))}
                                </select>
                                <label>Session</label>
                              </div>
                            </div>

                            {/* COURSE */}
                            <div className="col-md-4">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={selectedCourse}
                                  onChange={handleCourseChange}
                                  required
                                >
                                  <option value="">Select Course</option>
                                  {courses?.map((course) => (
                                    <option key={course?.id} value={course?.id}>
                                      {course?.title}
                                    </option>
                                  ))}
                                </select>
                                <label>Course</label>
                              </div>
                            </div>

                            {/* SUBJECT */}
                            <div className="col-md-4">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  value={selectedSubject}
                                  onChange={handleSubjectChange}
                                  disabled={!selectedCourse}
                                  required
                                >
                                  <option value="">Select Subject</option>
                                  {subjects?.map((subject) => (
                                    <option
                                      key={subject?.id}
                                      value={subject?.id}
                                    >
                                      {subject?.name}
                                    </option>
                                  ))}
                                </select>
                                <label>Subject</label>
                              </div>
                            </div>
                          </div>

                          {/* =====================================================
                              TOPIC
                          ===================================================== */}
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <span
                              className="badge bg-primary rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: 24, height: 24, fontSize: 12 }}
                            >
                              2
                            </span>
                            <h6 className="mb-0">Topic Details</h6>
                          </div>

                          <div className="border rounded-3 p-3 mb-2 bg-light bg-opacity-25">
                            {topics?.map((topic, index) => (
                              <div
                                key={index}
                                className="row g-2 align-items-center"
                              >
                                {/* TOPIC */}
                                <div className="col-md-3">
                                  <div className="form-floating form-floating-outline">
                                    <input
                                      type="text"
                                      className="form-control input-capitalize"
                                      value={topic?.topic}
                                      onChange={(e) =>
                                        handleTopicChange(
                                          index,
                                          "topic",
                                          e.target.value,
                                        )
                                      }
                                      required
                                    />
                                    <label>Topic</label>
                                  </div>
                                </div>

                                {/* DESCRIPTION */}
                                <div className="col-md-6">
                                  <div className="form-floating form-floating-outline">
                                    <input
                                      type="text"
                                      className="form-control input-uppercase"
                                      value={topic?.description}
                                      onChange={(e) =>
                                        handleTopicChange(
                                          index,
                                          "description",
                                          e.target.value,
                                        )
                                      }
                                    />
                                    <label>Description</label>
                                  </div>
                                </div>

                                {/* DURATION */}
                                <div className="col-md-3">
                                  <div className="form-floating form-floating-outline">
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={topic?.duration}
                                      min="1"
                                      onChange={(e) =>
                                        handleTopicChange(
                                          index,
                                          "duration",
                                          e.target.value,
                                        )
                                      }
                                      required
                                    />
                                    <label>
                                      <i className="ri-time-line me-1" />
                                      Duration (days)
                                    </label>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          <p className="text-muted small mb-4">
                            <i className="ri-information-line me-1" />
                            Changes apply only to this syllabus entry.
                          </p>

                          {/* =====================================================
                              BUTTONS - END / RIGHT
                          ===================================================== */}
                          <div className="d-flex justify-content-end border-top pt-3">
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate(-1)}
                                disabled={loading}
                              >
                                Cancel
                              </button>

                              <button
                                type="submit"
                                className="btn btn-primary px-4"
                                disabled={loading}
                              >
                                {loading ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                    />
                                    Updating...
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-save-line me-1" />
                                    Update Syllabus
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
